import { useState } from 'react';
import { extractApiErrorCode as apiCode } from '@/lib/api-client';
import { authContainer } from '../../di/container';

/** Firebase code (reauth). */
function fbCode(err: unknown): string {
  return typeof err === 'object' && err !== null && 'code' in err
    ? String((err as { code: unknown }).code)
    : '';
}

export interface StartEmailChangeResult {
  ok: boolean;
  /** Conta sem senha (422) — o front leva a "cadastre uma senha antes". */
  needsPassword: boolean;
}

/**
 * Ato 1 da troca de e-mail (modal): reautentica com a senha atual e dispara o
 * link ao e-mail **atual**. Os atos 2 e 3 acontecem nas páginas públicas, a
 * partir dos links.
 */
export function useChangeEmail() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const start = async (
    currentPassword: string,
  ): Promise<StartEmailChangeResult> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await authContainer.repository.startEmailChange(currentPassword);
      setSuccess(true);
      return { ok: true, needsPassword: false };
    } catch (err) {
      const code = fbCode(err);
      if (
        code === 'auth/wrong-password' ||
        code === 'auth/invalid-credential'
      ) {
        setError('Senha atual incorreta.');
      } else if (code === 'auth/too-many-requests') {
        setError('Muitas tentativas. Aguarde alguns minutos e tente de novo.');
      } else if (apiCode(err) === 'PASSWORD_METHOD_REQUIRED') {
        setError(
          'Você precisa cadastrar uma senha na sua conta antes de trocar o e-mail.',
        );
        return { ok: false, needsPassword: true };
      } else if (apiCode(err) === 'REAUTHENTICATION_REQUIRED') {
        setError('Confirme sua senha novamente e tente de novo.');
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Não foi possível iniciar a troca.',
        );
      }
      return { ok: false, needsPassword: false };
    } finally {
      setIsLoading(false);
    }
  };

  return { start, isLoading, error, success };
}
